// --- Our Modules --------------------------------------------------------------------------------
import config from "@/config/constants";
import type { Lang } from "@/hooks/useLang/types";
import useStore from "@/hooks/useStore";
import { orderLangs } from "@/utils";
import type { TyStoreLanguages } from "./types";

// --- Logic --------------------------------------------------------------------------------------

/**
 * The languages the **active store's menu** offers — not the dashboard's own UI language, which is
 * `useLang` and a different axis entirely.
 *
 * Every text input in the dashboard sizes itself from this, so it is read here once rather than each
 * screen re-deriving it from the persisted store.
 *
 * `CreateStoreScreen` is the one caller that cannot use it: the store does not exist yet, so it holds
 * the choice in local state and passes it down instead.
 */
export default function useStoreLanguages(): TyStoreLanguages {
    const store = useStore((state) => state.store);

    // Only reachable before the first store loads; the API has required a language since creation.
    const languages = orderLangs(store?.languages);
    const resolved = languages.length > 0 ? languages : (config.menuLanguages as readonly string[] as Lang[]);

    const stored = store?.defaultLang as Lang | undefined;
    const defaultLang = stored && resolved.includes(stored) ? stored : resolved[0];

    return { languages: resolved, defaultLang, isMulti: resolved.length > 1 };
}
